import {
  ADD_DESIGN_TO_EMAIL_TEMPLATE,
  AUTO_WIDTH_CTA_CLASS,
  BUTTON_DEFAULT_PRESET_ID,
  COMPONENT_LOADING_OVERLAY_CLASS,
  COMPONENT_LOADING_OVERLAY_WRAPPER_ID,
  CUSTOM_EVENT_COMPONENT_STYLE_COPIED,
  CUSTOM_EVENT_STORAGE_END,
  DESIGN_COPY,
  DESIGN_EDIT,
  DEFAULT_ZOOM,
  DEVICE_CLASS_PREFIX,
  DIVIDER_DEFAULT_HEIGHT,
  EMAIL_COMPONENT_DROPPABLE_KEY,
  EMAIL_DATA_CLIPBOARD_KEY,
  EMAIL_MAKER_BLOCKS,
  EMAIL_MAKER_BUTTON_PRESETS,
  EMAIL_MAKER_COMMANDS,
  EMAIL_MAKER_COMPONENT_TYPES,
  EMAIL_MAKER_PROPS,
  EMAIL_MAKER_STYLES,
  EMAIL_MEDIA_OVERLAY_ID,
  EMAIL_OVERLAY_ANIMATION_TIME_MS,
  EMAIL_OVERLAY_ID,
  EXPORT_OPTIONS_DIALOG,
  EXPORT_OPTIONS_DIALOG_MOBILE,
  HIGHLIGHT_HOVER_CLASS,
  LOCAL_STORAGE_POSTER_ID_KEY,
  LOCAL_STORAGE_POSTER_PAGE_ID_KEY,
  MAX_CONTENT_WIDTH,
  MAX_HTML_CONTENT_LENGTH,
  MAX_TRIES_TO_FIND_TOOLBAR,
  MAX_VIDEOS_ALLOWED,
  MINUTES_STEP,
  MOBILE_CSS_PROP_PREFIX,
  MORE_OPTIONS_DIALOG,
  NEW_TEMPLATE_NAME,
  NONCOPYABLE_STYLES,
  READ_ONLY_FOOTER_ID,
  RTE_TAGNAMES,
  TABLET_CSS_PROP_PREFIX,
  TOGGLE_OPTION_PRESET_ID_ONE,
  TOGGLE_OPTION_PRESET_ID_ZERO,
  WIDTH_DEFAULT_TOGGLE,
  ZOOM_LOWER_LIMIT,
  ZOOM_STEP,
  ZOOM_UPPER_LIMIT,
} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerCanvasDevice, EmailMakerExportOption, EmailMakerMediaImageModal, EmailMakerMediaType, EmailMakerMode} from '@EmailMakerReact/email-maker-react.types';
import type {default as EmailMakerConstants, CommandsTypes} from '@/types/email-maker-global.types';

// Shared components still read command/component-type ids off window.PMW.emails; this file provides that contract from the new tree's typed constants.

/**
 * Adjusts the global `window.PMW.emails` type where its declaration cannot be met by real values:
 * arrays widen to readonly (`as const` seed) and `commands` accepts both nested groups and flat
 * string commands. The `satisfies Partial<...>` below catches value-type drift.
 */
type SeedableEmailsNamespace = {
  [K in keyof EmailMakerConstants]: K extends 'commands'
    ? CommandsTypes & Record<string, string | Record<string, string>>
    : EmailMakerConstants[K] extends (infer U)[]
      ? readonly U[]
      : EmailMakerConstants[K];
};

/** The static subset of the legacy emails namespace the new tree provides to shared components. */
const SEEDED_EMAILS_NAMESPACE = {
  commands: EMAIL_MAKER_COMMANDS,
  blocks: EMAIL_MAKER_BLOCKS,
  components: EMAIL_MAKER_COMPONENT_TYPES,
  props: EMAIL_MAKER_PROPS,
  styles: EMAIL_MAKER_STYLES,
  buttonPresets: EMAIL_MAKER_BUTTON_PRESETS,
  mediaType: {
    IMAGE: EmailMakerMediaType.IMAGE,
    VIDEO: EmailMakerMediaType.VIDEO,
    ANIMATED_STICKER: EmailMakerMediaType.ANIMATED_STICKER,
    STILL_STICKER: EmailMakerMediaType.STILL_STICKER,
    ICON: EmailMakerMediaType.ICON,
  },
  mediaImageModal: {
    MY_UPLOADS: EmailMakerMediaImageModal.MY_UPLOADS,
    EXPLORE_MEDIA: EmailMakerMediaImageModal.EXPLORE_MEDIA,
    AI_IMAGES: EmailMakerMediaImageModal.AI_IMAGES,
  },
  exportOptions: {
    COPY_EMAIL: EmailMakerExportOption.COPY_EMAIL,
    COPY_HTML: EmailMakerExportOption.COPY_HTML,
    DOWNLOAD_HTML: EmailMakerExportOption.DOWNLOAD_HTML,
  },
  MODE_START: EmailMakerMode.START,
  MODE_LOAD: EmailMakerMode.LOAD,
  MODE_COPY: EmailMakerMode.COPY,
  DEVICE_DESKTOP: EmailMakerCanvasDevice.DESKTOP,
  DEVICE_TABLET: EmailMakerCanvasDevice.TABLET,
  DEVICE_MOBILE: EmailMakerCanvasDevice.MOBILE,
  ADD_DESIGN_TO_EMAIL_TEMPLATE,
  AUTO_WIDTH_CTA_CLASS,
  BUTTON_DEFAULT_PRESET_ID,
  COMPONENT_LOADING_OVERLAY_CLASS,
  COMPONENT_LOADING_OVERLAY_WRAPPER_ID,
  CUSTOM_EVENT_COMPONENT_STYLE_COPIED,
  CUSTOM_EVENT_STORAGE_END,
  DEFAULT_ZOOM,
  DESIGN_COPY,
  DESIGN_EDIT,
  DEVICE_CLASS_PREFIX,
  DIVIDER_DEFAULT_HEIGHT,
  EMAIL_COMPONENT_DROPPABLE_KEY,
  EMAIL_DATA_CLIPBOARD_KEY,
  EMAIL_MEDIA_OVERLAY_ID,
  EMAIL_OVERLAY_ANIMATION_TIME_MS,
  EMAIL_OVERLAY_ID,
  EXPORT_OPTIONS_DIALOG,
  EXPORT_OPTIONS_DIALOG_MOBILE,
  HIGHLIGHT_HOVER_CLASS,
  LOCAL_STORAGE_POSTER_ID_KEY,
  LOCAL_STORAGE_POSTER_PAGE_ID_KEY,
  MAX_CONTENT_WIDTH,
  MAX_HTML_CONTENT_LENGTH,
  MAX_TRIES_TO_FIND_TOOLBAR,
  MAX_VIDEOS_ALLOWED,
  MINUTES_STEP,
  MOBILE_CSS_PROP_PREFIX,
  MORE_OPTIONS_DIALOG,
  NEW_TEMPLATE_NAME,
  NONCOPYABLE_STYLES,
  READ_ONLY_FOOTER_ID,
  RTE_TAGNAMES,
  TABLET_CSS_PROP_PREFIX,
  TOGGLE_OPTION_PRESET_ID_ONE,
  TOGGLE_OPTION_PRESET_ID_ZERO,
  WIDTH_DEFAULT_TOGGLE,
  ZOOM_LOWER_LIMIT,
  ZOOM_STEP,
  ZOOM_UPPER_LIMIT,
} as const satisfies Partial<SeedableEmailsNamespace>;

/** Installs the shared-contract namespace and returns its teardown. Object.assign is used because a direct assignment of the static subset does not satisfy the full `EmailMakerConstants` interface type. */
export function seedEmailMakerNamespace(): () => void {
  Object.assign(window.PMW, {emails: SEEDED_EMAILS_NAMESPACE});
  return (): void => {
    Reflect.deleteProperty(window.PMW, 'emails');
  };
}
